import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tag, message } from 'antd';
import { EditOutlined, SendOutlined, WarningOutlined } from '@ant-design/icons';
import { useAgency } from '../../../hooks/useAgency';
import { agencyApi } from '../../../api/endpoints/agency.api';
import { PATHS } from '../../../routes/constants/paths';
import './RevisionRequestBanner.scss';

const RevisionRequestBanner: React.FC = () => {
  const navigate = useNavigate();
  const { agency, refreshAgency } = useAgency();
  const [submitting, setSubmitting] = useState(false);

  const status = String(agency?.status || '').toLowerCase();
  const profileStatus = String((agency as any)?.profileStatus || '').toLowerCase();
  const isRevision =
    status === 'revision_requested' || profileStatus === 'revision_requested';

  if (!isRevision) return null;

  const revisionNotes = String((agency as any)?.revisionNotes || '').trim();
  const revisionDate = (agency as any)?.revisionRequestedAt || null;
  const agencyId = agency?._id || (agency as any)?.id;

  const handleResubmit = async () => {
    if (!agencyId) return;
    setSubmitting(true);
    try {
      await agencyApi.resubmitProfile(agencyId);
      message.success('Profile resubmitted for review successfully');
      if (typeof refreshAgency === 'function') {
        refreshAgency();
      }
    } catch {
      message.error('Failed to resubmit profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditProfile = () => {
    if (typeof PATHS.AGENCY_ONBOARDING_STEP === 'function') {
      navigate(PATHS.AGENCY_ONBOARDING_STEP(1));
    } else {
      navigate('/agency/onboarding/step/1');
    }
  };

  const requestedOn = revisionDate
    ? new Date(revisionDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="revision-request-banner" role="status" aria-live="polite">
      <div className="revision-request-banner__icon" aria-hidden>
        <WarningOutlined />
      </div>

      <div className="revision-request-banner__body">
        <div className="revision-request-banner__header">
          <h3 className="revision-request-banner__title">Profile revision requested</h3>
          <Tag className="revision-request-banner__badge">Action required</Tag>
        </div>

        <p className="revision-request-banner__summary">
          An admin asked you to update your agency profile. Edit the requested fields, then
          resubmit for review.
        </p>

        {revisionNotes ? (
          <div className="revision-request-banner__notes">
            <span className="revision-request-banner__notes-label">Admin notes</span>
            <p className="revision-request-banner__notes-text">{revisionNotes}</p>
          </div>
        ) : null}

        {requestedOn ? (
          <p className="revision-request-banner__meta">Requested on {requestedOn}</p>
        ) : null}

        <div className="revision-request-banner__actions">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={handleEditProfile}
          >
            Edit Profile
          </Button>
          <Button
            size="small"
            icon={<SendOutlined />}
            loading={submitting}
            onClick={handleResubmit}
          >
            Resubmit for Review
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RevisionRequestBanner;
